import React, { useEffect, useRef, Suspense, useState } from 'react'; // Add useState
import { Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header } from '~/components/header';
import { Footer } from '~/components/footer';
// Remove direct import: import { NeonBackground } from '~/components/NeonBackground';
import { Toaster } from '~/components/ui/sonner';
import { useTheme } from '~/hooks/use-theme';
import { useSearch } from '~/contexts/search-context';
import { AuthInitializer } from '~/auth/components/auth-initializer';
import { gaMeasurementId, isGaEnabled } from '~/config/ga';
import { cn } from '~/lib/utils';

const NeonBackground = React.lazy(() =>
  import('~/components/NeonBackground').then((module) => ({
    default: module.NeonBackground,
  }))
);

export function AppLayout() {
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const { t } = useTranslation();
  const { searchExecuted } = useSearch();
  const metaUpdated = useRef(false);
  const { theme } = useTheme(); // Get theme for Toaster
  const [isMounted, setIsMounted] = useState(false); // Track client mount

  useEffect(() => {
    setIsMounted(true); // Set mounted state after initial render
  }, []); // Run only once

  // Debug logs
  useEffect(() => {
    console.log('[AppLayout] Component mounted', {
      pathname: location.pathname,
      isHomePage,
    });
    return () => {
      console.log('[AppLayout] Component unmounting');
    };
  }, [location.pathname, isHomePage]);

  // Update metadata translations
  useEffect(() => {
    if (!metaUpdated.current) {
      document.title = t('meta.title');

      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      const ogDescription = document.querySelector(
        'meta[property="og:description"]'
      );
      const twitterDescription = document.querySelector(
        'meta[name="twitter:description"]'
      );
      if (metaDescription)
        metaDescription.setAttribute('content', t('meta.description'));
      if (ogDescription)
        ogDescription.setAttribute('content', t('meta.description'));
      if (twitterDescription)
        twitterDescription.setAttribute('content', t('meta.description'));

      const metaTitle = document.querySelector('meta[property="og:title"]');
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaTitle) metaTitle.setAttribute('content', t('meta.title'));
      if (twitterTitle) twitterTitle.setAttribute('content', t('meta.title'));
      if (metaKeywords)
        metaKeywords.setAttribute('content', t('meta.keywords'));

      metaUpdated.current = true;
    }
  }, [t]);

  // Track Page Views with Google Analytics
  useEffect(() => {
    if (isGaEnabled && typeof window.gtag === 'function' && gaMeasurementId) {
      window.gtag('config', gaMeasurementId, {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return (
    <>
      {/* Always render the fallback div initially and on server */}
      <div className={cn('neon-background absolute inset-0 -z-10', theme)} />

      {/* Only render the Suspense/Lazy NeonBackground after mount and if width is sufficient */}
      {isMounted &&
        typeof navigator !== 'undefined' &&
        window.innerWidth > 768 && (
          <Suspense fallback={null}>
            {' '}
            {/* Fallback handled by the div above */}
            <NeonBackground paused={searchExecuted} />
          </Suspense>
        )}
      <Header alwaysShow={!isHomePage} searchExecuted={searchExecuted} />
      <main
        className={cn(
          'flex-1',
          !isHomePage && 'pt-24' // Add top padding for non-home pages to accommodate the fixed header
        )}
      >
        <Outlet />
      </main>
      <Footer />
      <Toaster
        richColors
        position="top-center"
        closeButton
        theme={theme === 'dark' ? 'dark' : 'light'}
      />
      <AuthInitializer />
    </>
  );
}
